import React, { FC, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Box,
  Avatar,
  Divider,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  IconButton,
  Badge,
  makeStyles
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  Dashboard as DashboardIcon,
  Person as ArtistsIcon,
  Videocam as StreamersIcon,
  AttachMoney as EarningsIcon,
  Schedule as ScheduledJobsIcon,
  AssignmentTurnedIn as ApprovalsIcon,
  SupervisorAccount as SuperAdminIcon,
  People as AffiliateIcon,
  Assessment as AnalyticsIcon,
  Payment as PaymentIcon,
  ImportExport as ConversionsIcon,
  Email as CommunicationsIcon,
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  ExitToApp as LogoutIcon,
} from '@material-ui/icons';
import { useHistory, useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';

import { useTokenManager } from '../../contexts/TokenManagerContext';
import CURRENT_USER from 'core/graphql/queries/currentUser';
import { CurrentUserQueryResponse } from 'core/graphql/queries/currentUser';
import { starfarmWordmarkUrl } from 'pages/Registration/registrationFlowStyles';

// Types
interface AdminLayoutProps {
  children: React.ReactNode;
}

interface NavigationItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  badge?: number;
  requiresAdmin?: boolean;
  section?: string;
}

const DRAWER_WIDTH = 240;

// Styles
const useStyles = makeStyles((theme) => ({
  shell: {
    display: 'flex',
    minHeight: '100vh',
    width: '100%',
    position: 'relative',
  },
  /** Desktop: plain column (not MUI permanent Drawer — that is `position: fixed` and fights our global CSS). */
  sidebarDesktop: {
    width: DRAWER_WIDTH,
    flexShrink: 0,
    minHeight: '100vh',
    backgroundColor: '#031d35',
    borderRight: '1px solid #2c4660',
    overflowY: 'auto',
    overflowX: 'hidden',
    zIndex: 1,
  },
  drawerPaper: {
    width: DRAWER_WIDTH,
    boxSizing: 'border-box',
    backgroundColor: '#031d35',
  },
  sidebarBrandBlock: {
    textAlign: 'center',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(2),
  },
  sidebarLogo: {
    width: 62,
    height: 62,
    objectFit: 'cover',
    borderRadius: '50%',
    display: 'block',
    margin: '0 auto',
    marginBottom: theme.spacing(1.5),
  },
  sidebarWordmark: {
    display: 'block',
    width: '70%',
    maxWidth: 150,
    height: 'auto',
    margin: '0 auto',
  },
  sidebarAdminCaption: {
    color: '#8ea5bf',
    marginTop: theme.spacing(1),
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    fontSize: '0.72rem',
    fontWeight: 700,
  },
  sidebarDivider: {
    backgroundColor: 'rgba(142, 165, 191, 0.28)',
  },
  drawerInner: {
    paddingBottom: theme.spacing(2),
  },
  /** Global App.css zeros padding on * — restore nav row layout for admin only */
  navListItem: {
    margin: theme.spacing(0.35, 0.9),
    paddingTop: theme.spacing(1.05),
    paddingBottom: theme.spacing(1.05),
    paddingLeft: theme.spacing(1.8),
    paddingRight: theme.spacing(1.5),
    borderRadius: 10,
  },
  navListIcon: {
    minWidth: 34,
  },
  content: {
    flex: '1 1 auto',
    minWidth: 0,
    boxSizing: 'border-box',
    padding: theme.spacing(3),
    backgroundColor: '#02172a',
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
  },
  navSection: {
    color: '#c5d4e8',
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    lineHeight: '36px',
  },
  navListSubheader: {
    backgroundColor: '#031d35',
    paddingLeft: theme.spacing(2),
  },
  /** Default nav colors (global theme text is too dark on `#0a1628`) */
  navIconDefault: {
    color: '#d0deee',
  },
  navIconActive: {
    color: '#dff2ff',
  },
  navTextDefault: {
    color: '#f0f6ff',
    fontWeight: 500,
  },
  navTextActive: {
    color: '#f7fbff',
    fontWeight: 700,
  },
}));

// Navigation items ordered to match production dashboard sections.
const navigationItems: NavigationItem[] = [
  {
    label: 'Dashboard',
    path: '/dashboard',
    icon: <DashboardIcon />,
  },
  {
    label: 'Artists',
    path: '/admin/artists',
    icon: <ArtistsIcon />,
    requiresAdmin: true,
  },
  {
    label: 'Streamers',
    path: '/admin/streamers',
    icon: <StreamersIcon />,
    requiresAdmin: true,
  },
  {
    label: 'Earnings',
    path: '/admin/earnings',
    icon: <EarningsIcon />,
    requiresAdmin: true,
  },
  {
    label: 'Scheduled Jobs',
    path: '/admin/scheduled-jobs',
    icon: <ScheduledJobsIcon />,
    requiresAdmin: true,
  },
  {
    label: 'Approvals',
    path: '/admin/approvals',
    icon: <ApprovalsIcon />,
    requiresAdmin: true,
  },
  {
    label: 'SuperAdmin',
    path: '/admin/superadmin',
    icon: <SuperAdminIcon />,
    requiresAdmin: true,
  },
  {
    label: 'Affiliate',
    path: '/admin/affiliate',
    icon: <AffiliateIcon />,
    requiresAdmin: true,
  },
  {
    label: 'Analytics',
    path: '/admin/analytics',
    icon: <AnalyticsIcon />,
    requiresAdmin: true,
  },
  {
    label: 'Payout Management',
    path: '/admin/payouts',
    icon: <PaymentIcon />,
    requiresAdmin: true,
  },
  {
    label: 'Conversions',
    path: '/admin/conversions',
    icon: <ConversionsIcon />,
    requiresAdmin: true,
  },
  {
    label: 'Communications',
    path: '/admin/communications',
    icon: <CommunicationsIcon />,
    requiresAdmin: true,
  },
];

const AdminLayout: FC<AdminLayoutProps> = ({ children }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const tokenManager = useTokenManager();
  const history = useHistory();
  const location = useLocation();

  // Check if user is admin
  const { data: userData, loading: userLoading } = useQuery<CurrentUserQueryResponse>(CURRENT_USER);
  const currentUser = userData?.user;
  
  // Fallback: check stored user data if GraphQL query fails
  const storedUser = localStorage.getItem('user');
  const storedUserData = storedUser ? JSON.parse(storedUser) : null;
  const storedIsAdminFlag = localStorage.getItem('isAdmin') === 'true' || localStorage.getItem('admin') === 'true';
  const isAdmin =
    currentUser?.role === 'admin' ||
    currentUser?.isAdmin === true ||
    storedUserData?.role === 'admin' ||
    storedUserData?.isAdmin === true ||
    storedIsAdminFlag;


  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path: string) => {
    history.push(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleLogout = () => {
    tokenManager.clearToken();
    localStorage.removeItem("admin");
    window.location.href = `${window.location.origin}/login`;
  };

  const isActiveRoute = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Filter navigation items based on admin permissions
  const filteredNavigationItems = navigationItems.filter(item => 
    !item.requiresAdmin || isAdmin
  );
  // Show loading state while checking permissions
  if (userLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography>Loading admin interface...</Typography>
      </Box>
    );
  }

  // Show access denied if not admin
  if (!isAdmin) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Alert severity="error" style={{ maxWidth: 400 }}>
          <Typography variant="h6" gutterBottom>
            Access Denied
          </Typography>
          <Typography>
            You don't have permission to access the admin interface. 
            Please contact your administrator if you believe this is an error.
          </Typography>
        </Alert>
      </Box>
    );
  }

  const drawer = (
    <div className={classes.drawerInner}>
      <Toolbar />
      {isMobile && <Toolbar />}
      <Box px={2} pb={1} pt={0} className={classes.sidebarBrandBlock}>
        <img src="/starFarm.png" alt="" className={classes.sidebarLogo} />
        <img src={starfarmWordmarkUrl} alt="StarFarm" className={classes.sidebarWordmark} />
        <Typography component="div" className={classes.sidebarAdminCaption}>
          Admin
        </Typography>
      </Box>
      <Divider className={classes.sidebarDivider} />
      <List>
        {filteredNavigationItems.map((item, index) => {
          const prevSection = index > 0 ? filteredNavigationItems[index - 1].section : undefined;
          const showHeader = item.section && item.section !== prevSection;
          return (
            <React.Fragment key={item.label}>
              {showHeader && (
                <ListSubheader
                  className={`${classes.navSection} ${classes.navListSubheader}`}
                  disableSticky
                >
                  {item.section!.toUpperCase()}
                </ListSubheader>
              )}
              <ListItem
                button
                className={classes.navListItem}
                onClick={() => handleNavigation(item.path)}
                selected={isActiveRoute(item.path)}
                style={{
                  backgroundColor: isActiveRoute(item.path) ? 'rgba(112, 171, 196, 0.34)' : 'transparent',
                }}
              >
                <ListItemIcon
                  className={`${classes.navListIcon} ${
                    isActiveRoute(item.path) ? classes.navIconActive : classes.navIconDefault
                  }`}
                >
                  {item.badge ? (
                    <Badge badgeContent={item.badge} color="secondary">
                      {item.icon}
                    </Badge>
                  ) : (
                    item.icon
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    variant: 'body2',
                    noWrap: false,
                    className: isActiveRoute(item.path) ? classes.navTextActive : classes.navTextDefault,
                  }}
                />
              </ListItem>
            </React.Fragment>
          );
        })}
      </List>
      <Divider className={classes.sidebarDivider} />
      <List>
        <ListItem button className={classes.navListItem} onClick={handleLogout}>
          <ListItemIcon className={`${classes.navListIcon} ${classes.navIconDefault}`}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText
            primary="Logout"
            primaryTypographyProps={{ variant: 'body2', className: classes.navTextDefault }}
          />
        </ListItem>
      </List>
    </div>
  );

  return (
    <Box className={`admin-app-shell ${classes.shell}`}>
      {isMobile && (
        <AppBar position="fixed" className={classes.appBar}>
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              style={{ marginRight: theme.spacing(2) }}
            >
              <MenuIcon />
            </IconButton>
            <Box style={{ flexGrow: 1 }} />
            <Box display="flex" alignItems="center">
              <IconButton color="inherit">
                <Badge badgeContent={4} color="secondary">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
              <Box display="flex" alignItems="center" ml={2}>
                <Avatar style={{ width: 32, height: 32, marginRight: 8 }}>
                  {currentUser?.firstName?.charAt(0) || 'A'}
                </Avatar>
                <Typography variant="body2">
                  {currentUser?.firstName} {currentUser?.lastName}
                </Typography>
              </Box>
            </Box>
          </Toolbar>
        </AppBar>
      )}

      {isMobile ? (
        <Drawer
          variant="temporary"
          anchor="left"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          classes={{
            paper: classes.drawerPaper,
          }}
        >
          {drawer}
        </Drawer>
      ) : (
        <Box component="aside" className={classes.sidebarDesktop}>
          {drawer}
        </Box>
      )}

      <Box component="div" className={classes.content}>
        {isMobile && <Toolbar />}
        {children}
      </Box>
    </Box>
  );
};

export default observer(AdminLayout); 